import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Typography, IconButton, Tooltip, Button } from '@material-ui/core';
import { hasPermission, createHistory, hasAtLeastOnePermission, formatDate } from '../../sc-utilities';
import { FindInPage } from '@material-ui/icons';
import BillingAccountList from './BillingAccountList';
import { CustomChip } from '../../sc-components';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    progressBarr: {
        width: '100%',
        height: 5
    },
    "@global": {
        "[class*='chipActive']": {
            color: theme.palette.success.dark
        },
        "[class*='chipPrepared']": {
            color: theme.palette.warning.dark
        },
        "[class*='chipInactive']": {
            color: theme.palette.error.dark
        },
        "[class*='paletteButtons']": {
            color: theme.palette.primary.main,
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.buttonText.default
            }
        }
    },
    chipActive: {
        color: theme.palette.success.dark,
    },
    chipPrepared: {
        color: theme.palette.warning.dark,
    },
    chipInactive: {
        color: theme.palette.error.dark,
    },
    paletteButtons: {
        color: theme.palette.primary.main,
        margin: theme.spacing(),
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        },
    }
});

const BillingAccount = props => {
    const tableRef = React.createRef();
    const { classes, columns, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, billingAccountID: null, redirectPath: '' });

    const redirectToCustomer = id => {
        createHistory();
        setRedirectOptions({
            redirect: true,
            customerID: id,
            redirectPath: `/admin/customers/details?customerID=${id}`
        });
    };

    const getFormattedBillingMode = useCallback((BillingMode) => {
        switch (BillingMode) {
            case 'prepaid':
                return translations['prepaid'] || 'Prepaid';
            case 'postpaid':
                return translations['contract'] || 'Contract';
            default:
                return translations['unknown'] || 'Unknown';
        }
    }, [translations]);

    const getFormattedStatus = useCallback((id, status) => {
        switch (status) {
            case 'active':
                return <CustomChip key={`${id}`} className={classes['chipActive']} value={translations['active'] || 'Active'} />;
            case 'inactive':
                return <CustomChip key={`${id}`} className={classes['chipInactive']} value={translations['inactive'] || 'Inactive'} />;
            case 'prepared':
                return <CustomChip key={`${id}`} className={classes['chipPrepared']} value={translations['prepared'] || 'Prepared'} />;
            default:
                return '';
        }
    }, [translations, classes]);

    let visible = columns && columns['BillingAccount'];
    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: rowData => <>
                    {hasPermission(permissions, 'billing_account_load') ?
                        <Tooltip key={`Edit-${rowData.AccountID}`} title={translations["go_account_overview"] || "Go to account overview"}>
                            <IconButton
                                id={`edit_billing_account_${rowData.AccountID}`}
                                component='div'
                                onClick={() => redirectHandler(`/billing/accounts-overview?billingAccountID=${rowData.AccountID}`)}
                            >
                                <FindInPage />
                            </IconButton>
                        </Tooltip>
                        : null
                    }
                </>
            },
            {
                title: translations["customer_name"] || 'Customer name', field: 'CustomerName',
                hidden: visible ? visible['CustomerName'] : false,
                filtering: true,
                render: rowData => {
                    return rowData.Customer ? (
                        <Button
                            key={rowData.Customer.CustomerID}
                            className={classes.paletteButtons}
                            size="small"
                            onClick={() => redirectToCustomer(rowData.Customer.CustomerID)}
                        >
                            {' '}
                            {rowData.Customer.Name}
                        </Button>) : <Typography variant={'caption'}>translations['short_not_assigned'] || 'n/a'</Typography>;
                }
            },
            {
                title: translations["billing_mode"] || 'Billing mode', field: 'BillingMode',
                hidden: visible ? visible['BillingMode'] : false,
                lookup: { 'prepaid': translations['prepaid'] || 'Prepaid', 'postpaid': translations['contract'] || 'Contract' },
                defaultFilter: ['prepaid', 'postpaid'],
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.BillingMode ? getFormattedBillingMode(rowData.BillingMode) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["activation_date"] || 'Activation date', field: 'ActivationDate',
                hidden: visible ? visible['ActivationDate'] : false,
                type: 'date',
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.ActivationDate ? formatDate(rowData.ActivationDate) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["billing_day"] || 'Billing day', field: 'BillingDay',
                hidden: visible ? visible['BillingDay'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.BillingDay ? `${translations['on_every'] || 'On every'} ${rowData.BillingDay}.` : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["expires_at"] || 'Expires at', field: 'ExpiresAt',
                hidden: visible ? visible['ExpiresAt'] : false,
                type: 'date',
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.ExpiresAt ? formatDate(rowData.ExpiresAt) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["status"] || 'Status', field: 'Status',
                hidden: visible ? visible['Status'] : false,
                lookup: { 'active': translations['active'] || 'Active', 'prepared': translations['prepared'] || 'Prepared', 'inactive': translations['inactive'] || 'Inactive' },
                defaultFilter: ['active', 'prepared', 'inactive'],
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Status ? getFormattedStatus(rowData.AccountID, rowData.Status) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["deleted"] || 'Deleted', field: 'Deleted',
                hidden: visible ? visible['Deleted'] : false,
                lookup: { '0': translations['no'] || 'No', '1': translations['yes'] || 'Yes' },
                defaultFilter: ['0'],
                filtering: true,
                width: 100,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            }
        ];
    }, [classes, permissions, translations, getFormattedBillingMode, getFormattedStatus, visible]);


    if (!hasAtLeastOnePermission(permissions, ['billing_account_create', 'billing_account_load', 'billing_account_assign_plans'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, billingAccountID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, billingAccountID: billingAccountID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'billing_account_list') ?
                <BillingAccountList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                />
                :
                null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(BillingAccount));